import React, { useEffect, useState } from 'react';
import { Button, Table } from 'react-bootstrap-5';

import { offMarketAssociatedPropertiesCount } from 'bundles/connect/helpers/prospect_api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser } from '@fortawesome/pro-solid-svg-icons';
import LlcDetails from './llc_details';
import { isEmpty } from 'lodash';
import type { FAAddress, FAOwner } from 'bundles/connect/types';
import { useSelector } from 'react-redux';
import { selectOffMarketOcCompany } from '../store/selectors/off_market_property';
import { selectCallConsoleOcCompany } from '../store/selectors/call_console';
import RethinkMode from 'bundles/connect/components/rethink_mode/rethink_mode';

type Props = {
  faOwners?: FAOwner[];
  fromConsole?: boolean;
  ownerOccupied?: boolean;
  ownerAddress?: FAAddress;
  insightsPropertyId: string;
}

export default function Ownership(
  { faOwners, fromConsole = false, ownerOccupied, ownerAddress, insightsPropertyId }: Props
) {
  const llcData = useSelector(fromConsole ? selectCallConsoleOcCompany : selectOffMarketOcCompany);
  const [associatedPropertiesCount, setAssociatedPropertiesCount] = useState(0);
  const owner = faOwners?.[0];
  const otherOwner = faOwners?.[1];

  const hasFullAddress = [
    ownerAddress?.address,
    ownerAddress?.city,
    ownerAddress?.state,
    ownerAddress?.zip
  ].every(val => !isEmpty(val));

  const fullAddress = hasFullAddress
    ? `${ownerAddress?.address}, ${ownerAddress?.city}, ${ownerAddress?.state} ${ownerAddress?.zip}`
    : null;

  useEffect(() => {
    offMarketAssociatedPropertiesCount(insightsPropertyId)
      .then(setAssociatedPropertiesCount);
  }, []);

  const tableRow = (label, value) => {
    if (!value) return null;

    return (
      <tr>
        <td className="fw-bold">{label}</td>
        <td>
          <div className="d-flex align-items-center justify-content-between">
            {value}
          </div>
        </td>
      </tr>
    );
  };

  const associatedPropertiesUrl = '/prospect/search?' + [
    `ownerAddress=${ownerAddress?.address || ''}`,
    `ownerCity=${ownerAddress?.city || ''}`,
    `ownerState=${ownerAddress?.state || ''}`,
    `ownerZip=${ownerAddress?.zip || ''}`,
    'associated_search=true'
  ].join('&');

  const associatedPropertiesLink = (
    associatedPropertiesCount > 1 ? (
      <a
        className="text-decoration-underline"
        href={associatedPropertiesUrl}
        rel="noopener noreferrer"
        target="_blank"
      >
        {associatedPropertiesCount} Properties
      </a>
    ) : 'No Properties Available'
  );

  const rethinkAssociatedPropertiesLink = (
    associatedPropertiesCount > 1 ? (
      <Button
        as="a"
        bsPrefix="btn-link"
        className="text-decoration-underline p-0"
        onClick={() => window.location.href = associatedPropertiesUrl}
      >
        {associatedPropertiesCount} Properties
      </Button>
    ) : 'No Properties Available'
  );

  if (!owner || !owner.fullName?.trim() || owner.fullName.includes('Not Available')) return null;

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center gap-2 mb-2">
        <FontAwesomeIcon className="text-secondary" icon={faCircleUser} />
        <h6 className="m-0 fw-bolder">Ownership</h6>
      </div>
      <Table>
        <tbody>
          {tableRow('Owner Name', <span className="privacy-mask">{owner.fullName}</span>)}
          {tableRow('Owner Type', (owner.isCorporation ? 'Company' : 'Private'))}
          {tableRow('Owner Occupies Property', (ownerOccupied ? 'Yes' : 'No'))}
          {fullAddress && tableRow('Owner Address', <span className="privacy-mask">{fullAddress}</span>)}
          {otherOwner && tableRow('Other Owner', <span className="privacy-mask">{otherOwner.fullName}</span>)}
          <RethinkMode>{tableRow('Associated Properties Nationally', associatedPropertiesLink)}</RethinkMode>
          <RethinkMode display>
            {tableRow('Associated Properties Nationally', rethinkAssociatedPropertiesLink)}
          </RethinkMode>
        </tbody>
      </Table>
      {llcData && <LlcDetails llcData={llcData} />}
    </div>
  );
}
